import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './ResetPassword.css';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: 'var(--text-primary)',
        borderRadius: 2,
        '& fieldset': { borderColor: '#2d3344' },
        '&:hover fieldset': { borderColor: '#4a5568' },
        '&.Mui-focused fieldset': { borderColor: 'var(--brand-strong)' },
    },
    '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--brand-strong)' },
    '& .MuiFormHelperText-root': { color: 'var(--text-muted)' },
};

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { resetPassword } = useAuth();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            const data = await resetPassword(token, password);
            setSuccess(data.message || 'Your password has been reset.');
            setTimeout(() => {
                navigate('/login', { replace: true });
            }, 1500);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(detail || 'Could not reset your password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="reset-password-page">
            <Helmet>
                <title>Reset Password | Hockey Pipeline</title>
            </Helmet>

            <Card className="reset-password-card" elevation={0}>
                <CardContent sx={{ p: 3 }}>
                    <div className="reset-password-logo">
                        <img src="/logo@4x.png" alt="Hockey Pipeline" />
                    </div>

                    <Typography variant="h4" component="h1" className="reset-password-title">
                        Reset Password
                    </Typography>

                    {!token ? (
                        <>
                            <Alert
                                severity="error"
                                sx={{
                                    mt: 2,
                                    borderRadius: 2,
                                    backgroundColor: '#2d1515',
                                    color: '#fca5a5',
                                    '& .MuiAlert-icon': { color: '#f87171' },
                                }}
                            >
                                This reset link is missing a token.
                            </Alert>
                            <Button
                                component={RouterLink}
                                to="/login"
                                fullWidth
                                variant="contained"
                                className="reset-password-button"
                                disableElevation
                            >
                                Back to Sign In
                            </Button>
                        </>
                    ) : success ? (
                        <Alert
                            severity="success"
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                backgroundColor: '#102417',
                                color: '#bbf7d0',
                                '& .MuiAlert-icon': { color: '#86efac' },
                            }}
                        >
                            {success}
                        </Alert>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Typography variant="body2" sx={{ color: 'var(--text-muted)', mb: 2 }}>
                                Choose a new password for your account.
                            </Typography>

                            <TextField
                                fullWidth
                                required
                                label="New password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                margin="normal"
                                autoComplete="new-password"
                                helperText="Minimum 8 characters"
                                sx={fieldSx}
                            />
                            <TextField
                                fullWidth
                                required
                                label="Confirm new password"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                margin="normal"
                                autoComplete="new-password"
                                sx={fieldSx}
                            />

                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mt: 2,
                                        borderRadius: 2,
                                        backgroundColor: '#2d1515',
                                        color: '#fca5a5',
                                        '& .MuiAlert-icon': { color: '#f87171' },
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading}
                                className="reset-password-button"
                                disableElevation
                            >
                                {loading ? (
                                    <CircularProgress size={22} sx={{ color: '#102014' }} />
                                ) : (
                                    'Reset Password'
                                )}
                            </Button>

                            <Typography
                                variant="body2"
                                align="center"
                                sx={{ mt: 2, color: '#6b7280' }}
                            >
                                <RouterLink to="/login" className="reset-password-link">
                                    Back to Sign In
                                </RouterLink>
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default ResetPassword;
